import { useState, useEffect } from "react";
import { ArrowRight, Shield, Clock, Award, MapPin, Phone, CheckCircle, TrendingUp, TrendingDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import RateBoard from "@/components/RateBoard";
import CurrencyConverter from "@/components/CurrencyConverter";
import BankComparison from "@/components/BankComparison";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Crypto icon mapping
const getCryptoIcon = (symbol) => {
  const icons = {
    BTC: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
    ETH: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
    SOL: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
    ADA: "https://assets.coingecko.com/coins/images/975/small/cardano.png",
  };
  return icons[symbol] || "https://assets.coingecko.com/coins/images/1/small/bitcoin.png";
};

// Mini sparkline component
const MiniChart = ({ isPositive }) => (
  <svg viewBox="0 0 100 30" className="w-20 h-8">
    <path
      d={isPositive 
        ? "M0,25 Q10,22 20,20 T40,15 T60,18 T80,10 T100,5"
        : "M0,5 Q10,8 20,10 T40,15 T60,12 T80,20 T100,25"
      }
      fill="none"
      stroke={isPositive ? "#10B981" : "#EF4444"}
      strokeWidth="2"
    />
  </svg>
);

export default function Home() {
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [loadingCrypto, setLoadingCrypto] = useState(true);

  // Fetch crypto prices
  useEffect(() => {
    const fetchCrypto = async () => {
      try {
        const res = await axios.get(`${API}/api/crypto/prices`);
        // Get top 4 cryptos: BTC, ETH, SOL, ADA
        const prices = res.data.prices || [];
        const top4 = ['BTC', 'ETH', 'SOL', 'ADA'];
        const filtered = top4.map(symbol => 
          prices.find(p => p.symbol === symbol)
        ).filter(Boolean);
        setCryptoPrices(filtered);
      } catch (error) {
        console.error("Error fetching crypto:", error);
      }
      setLoadingCrypto(false);
    };
    fetchCrypto();
  }, []);

  // Format price
  const formatPrice = (price) => {
    if (price >= 1000) return price.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toFixed(2);
    return price.toFixed(4);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section - Dark Blue */}
      <section className="bg-[#001435]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left Content */}
            <div className="space-y-8 pt-8">
              <div className="space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
                  Currency exchange
                  <br />
                  <span className="text-blue-400">made simple</span>
                </h1>
                <p className="text-lg md:text-xl text-blue-100/70 max-w-xl leading-relaxed">
                  Competitive rates on fiat currencies and cryptocurrency. 
                  Fast, secure transactions at our New Westminster location.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#rates" className="btn-primary inline-flex items-center justify-center">
                  View Today's Rates
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <Link to="/contact" className="btn-secondary inline-flex items-center justify-center">
                  Contact Us
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 pt-4">
                <div className="trust-badge">
                  <Shield className="w-4 h-4" />
                  <span>FINTRAC Registered</span>
                </div>
                <div className="trust-badge">
                  <CheckCircle className="w-4 h-4" />
                  <span>100% Secure</span>
                </div>
              </div>

              {/* Location Info */}
              <div className="flex flex-col sm:flex-row gap-6 pt-4 text-blue-100/60 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>2-723 12th St, New Westminster, BC</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+17787590400" className="hover:text-white transition-colors">+1 (778) 759-0400</a>
                </div>
              </div>
            </div>

            {/* Right - Rate Board */}
            <div>
              <RateBoard />
            </div>
          </div>
        </div>
        
        {/* Desktop: Curved white section with converter centered on curve */}
        <div className="relative hidden md:block -mt-24">
          <svg className="w-full h-72 block" viewBox="0 0 1440 288" preserveAspectRatio="none" style={{ display: 'block' }}>
            <path fill="white" d="M0,288 L0,144 Q720,0 1440,144 L1440,288 Z"></path>
          </svg>
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-20 px-4">
            <CurrencyConverter />
          </div>
        </div>
        {/* White extension to ensure no gap/line */}
        <div className="hidden md:block bg-white h-4 -mt-1"></div>
      </section>

      {/* Mobile only: Clean white section with converter */}
      <div className="md:hidden bg-white py-6 px-4">
        <CurrencyConverter />
      </div>

      {/* Spacer for gap between converter and crypto section - seamless white background */}
      <div className="bg-white h-16 md:h-20"></div>

      {/* Live Crypto Spot Prices Section */}
      <section className="bg-[#001435] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Live Spot Prices</h2>
              <p className="text-blue-100/60">Real-time cryptocurrency prices in CAD</p>
            </div>
            <Link 
              to="/crypto" 
              className="flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-5 py-2.5 rounded-lg transition-colors font-medium"
            >
              View All Prices
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loadingCrypto ? (
              // Skeleton loaders
              [...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-slate-200 rounded-full" />
                    <div className="h-5 bg-slate-200 rounded w-20" />
                  </div>
                  <div className="h-8 bg-slate-200 rounded w-32 mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-16" />
                </div>
              ))
            ) : (
              cryptoPrices.map((crypto) => (
                <Link 
                  key={crypto.id} 
                  to="/crypto"
                  className="bg-white rounded-2xl p-5 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img 
                        src={getCryptoIcon(crypto.symbol)} 
                        alt={crypto.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <p className="font-semibold text-slate-900">{crypto.name}</p>
                        <p className="text-slate-500 text-sm">{crypto.symbol}</p>
                      </div>
                    </div>
                    <span className={`flex items-center gap-1 text-sm font-medium ${
                      crypto.change_24h >= 0 ? 'text-emerald-500' : 'text-red-500'
                    }`}>
                      {crypto.change_24h >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {crypto.change_24h >= 0 ? '+' : ''}{crypto.change_24h?.toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mb-2">
                    C${formatPrice(crypto.price_cad)}
                  </p>
                  <MiniChart isPositive={crypto.change_24h >= 0} />
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* How It Works - White Background */}
      <section className="bg-white pt-16 md:pt-24 pb-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How to exchange currency
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Simple, fast, and secure currency exchange in three steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="step-number-light mx-auto mb-6">1</div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Check Our Rates</h3>
              <p className="text-slate-600">
                View competitive buy and sell rates for all currencies on our rate board.
              </p>
            </div>
            <div className="text-center">
              <div className="step-number-light mx-auto mb-6">2</div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Visit Our Location</h3>
              <p className="text-slate-600">
                Come to our New Westminster office. No appointment needed.
              </p>
            </div>
            <div className="text-center">
              <div className="step-number-light mx-auto mb-6">3</div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Complete Exchange</h3>
              <p className="text-slate-600">
                Bring valid ID and funds. We complete your exchange quickly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bank Comparison Section */}
      <BankComparison />

      {/* Why Choose Us */}
      <section className="section-light py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1637497578001-63efa6b0acb2?w=700&h=500&fit=crop"
                alt="Currency Exchange"
                className="rounded-2xl shadow-2xl"
              />
            </div>

            {/* Right - Features */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Why choose FonderX?
                </h2>
                <p className="text-slate-600 text-lg">
                  Trusted currency exchange services in New Westminster.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Competitive Rates</h3>
                    <p className="text-slate-600 text-sm">Best exchange rates in the Greater Vancouver area.</p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Licensed & Secure</h3>
                    <p className="text-slate-600 text-sm">Registered MSB compliant with FINTRAC regulations.</p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Fast Service</h3>
                    <p className="text-slate-600 text-sm">No appointment needed. Walk in and exchange quickly.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="hero-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to exchange?
          </h2>
          <p className="text-blue-100/70 mb-8 text-lg max-w-2xl mx-auto">
            Visit our location in New Westminster or contact us for more information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://maps.google.com/?q=2-723+12th+Street+New+Westminster+BC" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Get Directions
            </a>
            <Link to="/contact" className="btn-secondary inline-flex items-center justify-center">
              <Phone className="w-4 h-4 mr-2" />
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
